import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs } from 'antd';
import { useAuthStore, useChannelStore, useAgentStore, useServerStore } from '../stores';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { InviteAgentForm, InviteAgentResult } from './InviteAgentModal';
import { AGENT_PROVIDERS, AGENT_PROVIDER_LABELS, buildAgentConnectCommand, buildAgentConnectCommandParts, providerHasPlatformDifference, getStoredAgentProvider, setStoredAgentProvider, type AgentProvider, type Platform } from './AgentConnectResult';
import { useToast } from './Toast';
import {
  fetchAgent,
  createAgent,
  DEFAULT_PROJECTION_STRATEGY,
  fetchRemoteNodes,
  fetchRemoteNodeStatus,
  fetchGuilds,
  fetchGuildChannels,
  addGuildMember,
  rotateAgentApiKey,
  revealAgentApiKey,
  fetchAgentPermissions,
  fetchAgentConfig,
  updateAgentConfig,
  updateAgentPermissions,
  updateAgentRequireMention,
  addAgentToChannel,
  getAgentChannels,
  fetchAgentRuntime,
  setAgentChannelProjectionStrategy,
  type Agent,
  type AgentChannelSummary,
  type AgentRuntime,
  type ClaudePromptStrategy,
  type PermissionDetail,
  type HostedAgentProvider,
  type RemoteNode,
  type Guild,
} from '../lib/api';
import type { Channel } from '../types';
import DecommissionAgentModal from './DecommissionAgentModal';
import { channelDisplayName } from '../lib/channelDisplay';
import { getBorgeeHttpBaseUrl } from '../lib/runtime-base-url';
import { buildOpenClawOnboardingPlan } from '../lib/openclaw-onboarding';
import { routes } from '../lib/routes';
import { CopyOutlined } from '@ant-design/icons';
import { UserBadge } from './UserBadge';
import RuntimeCard from './RuntimeCard';
import { AgentConfigPanel } from './AgentConfigPanel';
import { copyToClipboard, readClipboard } from '../lib/clipboard';
import { usePresence } from '../hooks/usePresence';

// #684 — Mask helper for API keys.
//
// Render `bgr_...{last4}` (prefix + ... + last four chars) to avoid copying
// OpenAI-style `sk-` examples. The last four chars are enough to identify the
// key but not enough to brute-force it.
//
// Constraint: the full plaintext key must not enter the DOM. This helper accepts
// only the last four chars; callers slice after fetch and immediately drop the
// full key so it cannot be kept in React state.
//
// 前缀 `bgr_` 写死 — server-go `GenerateAPIKey()` actual value (queries_phase2b.go:440).
// Matches brief §2.3 + §2.4 grep guards; `sk-` must not appear.
function formatMaskedApiKey(last4: string): string {
  return `bgr_...${last4}`;
}

// #684 — Auto-clear delay: 60 seconds, matching the security design and common
// password-manager behavior.
const API_KEY_AUTO_CLEAR_MS = 60_000;

const KNOWN_PERMISSIONS = [
  'message.send',
  'channel.create',
  'channel.delete',
  'channel.manage_members',
  'channel.manage_visibility',
];
const HOSTED_AGENT_PROVIDERS: HostedAgentProvider[] = ['claude', 'codex', 'copilot'];
const CLAUDE_PROMPT_STRATEGIES: ClaudePromptStrategy[] = [
  'session-brief',
  'turn-full',
  'message-only',
  'turn-thin',
  'file-brief',
];

function resolveConnectedHostedProvider(agent: Agent): HostedAgentProvider | null {
  const match = agent.last_connected_plugin_id?.match(/^agents-host:(claude|codex|copilot)$/);
  return match ? (match[1] as HostedAgentProvider) : null;
}

interface Props {
  onBack?: () => void;
}

export default function AgentManager({ onBack }: Props) {
  const channels = useChannelStore(s => s.channels);
  const agents = useAgentStore(s => s.agents);
  const loadAgents = useAgentStore(s => s.loadAgents);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [decommissionAgent, setDecommissionAgent] = useState<Agent | null>(null);

  const load = useCallback(async () => {
    try {
      await loadAgents();
    } finally {
      setLoading(false);
    }
  }, [loadAgents]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="agent-page">
      <div className="admin-section-header">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {onBack && <button className="btn btn-sm" onClick={onBack}>← Back</button>}
          <h2>My Agents</h2>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>Create Agent</button>
      </div>

      {loading ? (
        <div className="app-loading"><div className="loading-spinner-large" /></div>
      ) : agents.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
          No agents yet. Create one to get started.
        </p>
      ) : (
        agents.map(agent => (
          <AgentCard
            key={agent.id}
            agent={agent}
            expanded={selectedAgent === agent.id}
            onToggle={() => setSelectedAgent(selectedAgent === agent.id ? null : agent.id)}
            onDelete={() => setDecommissionAgent(agent)}
            onRefresh={load}
            channels={channels}
          />
        ))
      )}

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {decommissionAgent && (
        <DecommissionAgentModal
          agentId={decommissionAgent.id}
          agentName={decommissionAgent.display_name}
          agentState={decommissionAgent.state}
          lastConnectedPluginId={(decommissionAgent as any).last_connected_plugin_id}
          onClose={() => setDecommissionAgent(null)}
          onDeleted={() => {
            setDecommissionAgent(null);
            useAgentStore.getState().loadAgents();
            const guildId = useAuthStore.getState().guildId;
            if (guildId) void Promise.resolve(useServerStore.getState().loadMembers(guildId)).catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}

function AgentCard({
  agent,
  expanded,
  onToggle,
  onDelete,
  onRefresh,
  channels,
}: {
  agent: Agent;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  channels: { id: string; name: string; type?: 'channel' | 'dm' }[];
}) {
  const [permissions, setPermissions] = useState<PermissionDetail[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  // require_mention 本地镜像 — 翻转时立即 setOptimistic, 服务器失败回滚原值.
  // 默认 true 跟 server users.require_mention DEFAULT true 同源 (model.go:50);
  // 老 agent / undefined 字段也按 true 渲染 — 跟 server 兜底一致.
  const [requireMention, setRequireMention] = useState<boolean>(agent.require_mention ?? true);
  const [savingRequireMention, setSavingRequireMention] = useState(false);
  useEffect(() => {
    setRequireMention(agent.require_mention ?? true);
  }, [agent.require_mention]);
  // #684 — API key display policy. Constraint: the full plaintext key never
  // enters React state or refs. It is held only inside the fetch closure, sliced
  // with `key.slice(-4)`, then discarded.
  //
  // Previous visibleKey / newKey state was removed because it stored the full plaintext key.
  const [last4, setLast4] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [copying, setCopying] = useState(false);
  const [connectProvider, setConnectProvider] = useState<AgentProvider>(() => getStoredAgentProvider(agent.id) ?? 'openclaw');
  const [connectPlatform, setConnectPlatform] = useState<Platform>('windows');
  const [copyingConnect, setCopyingConnect] = useState(false);
  // 60s auto-clear timer ID, 用 ref 避免 re-render 干扰 cleanup (跟 #695
  // useUnsavedChangesGuard useRef 思路一致, 反 closure staleness).
  const autoClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToast();
  const [joinChannelId, setJoinChannelId] = useState('');
  const [managedProvider, setManagedProvider] = useState<HostedAgentProvider | null>(
    () => resolveConnectedHostedProvider(agent),
  );
  const [agentChannels, setAgentChannels] = useState<AgentChannelSummary[]>([]);
  const [loadingAgentChannels, setLoadingAgentChannels] = useState(false);
  const [savingChannelStrategyId, setSavingChannelStrategyId] = useState<string | null>(null);
  const [channelStrategyDrafts, setChannelStrategyDrafts] = useState<Record<string, ClaudePromptStrategy>>({});

  // AL-4.3 (#379 §1 split): runtime card state. fetchAgentRuntime returns
  // null when the agent has not registered a runtime yet (graceful degradation,
  // design ① "Borgee 不带 runtime"). Load only when expanded to avoid one
  // request per list row.
  const currentUser = useAuthStore(s => s.currentUser);
  const viewerUserID = currentUser?.id ?? null;
  const [runtime, setRuntime] = useState<AgentRuntime | null>(null);
  const [runtimeLoaded, setRuntimeLoaded] = useState(false);
  const loadRuntime = useCallback(async () => {
    try {
      const rt = await fetchAgentRuntime(agent.id);
      setRuntime(rt);
    } catch {
      // Silent failure: #190 §11 prefers silence over synthetic progress; a transient error
      // should not block the expanded panel.
      setRuntime(null);
    } finally {
      setRuntimeLoaded(true);
    }
  }, [agent.id]);

  const loadPerms = useCallback(async () => {
    setLoadingPerms(true);
    try {
      const data = await fetchAgentPermissions(agent.id);
      setPermissions(data.details);
    } finally {
      setLoadingPerms(false);
    }
  }, [agent.id]);

  // F7 (#1108) — Load the API key mask on expand. The read response now
  // carries only api_key_last4 (the full key was removed from reads). Store
  // that 4-char string directly; the full key never enters this component
  // until the owner explicitly copies (revealAgentApiKey).
  const loadKeyMask = useCallback(async () => {
    setLoadingKey(true);
    try {
      const data = await fetchAgent(agent.id);
      // Constraint: read api_key_last4 directly; reads no longer return the full key.
      if (typeof data.api_key_last4 === 'string' && data.api_key_last4.length >= 4) {
        setLast4(data.api_key_last4.slice(-4));
      } else {
        setLast4(null);
      }
    } catch {
      // Failure is shown inline only, matching RuntimeCard's no synthetic progress pattern.
      setLast4(null);
    } finally {
      setLoadingKey(false);
    }
  }, [agent.id]);

  const loadAgentChannelSettings = useCallback(async () => {
    setLoadingAgentChannels(true);
    try {
      const channelRows = await getAgentChannels(agent.id);
      setAgentChannels(channelRows);
      setChannelStrategyDrafts(
        Object.fromEntries(
          channelRows.map(channel => [channel.id, channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY]),
        ),
      );
    } catch {
      setAgentChannels([]);
      setChannelStrategyDrafts({});
    } finally {
      setLoadingAgentChannels(false);
    }
  }, [agent.id]);

  useEffect(() => {
    if (expanded) {
      loadPerms();
      loadRuntime();
      loadKeyMask();
      void (async () => {
        try {
          const config = await fetchAgentConfig(agent.id);
          setManagedProvider(config.blob.hosted?.provider ?? resolveConnectedHostedProvider(agent));
        } catch {
          setManagedProvider(resolveConnectedHostedProvider(agent));
        }
      })();
      void loadAgentChannelSettings();
    }
  }, [agent, expanded, loadAgentChannelSettings, loadKeyMask, loadPerms, loadRuntime]);

  useEffect(() => {
    setManagedProvider(resolveConnectedHostedProvider(agent));
  }, [agent]);

  // #684 — Clean up the auto-clear timer on unmount so it cannot fire after the
  // user leaves the sidepane or refreshes. Cleanup does not write to the
  // clipboard; after unmount, clipboard contents remain under user control.
  useEffect(() => {
    return () => {
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
        autoClearTimerRef.current = null;
      }
    };
  }, []);

  // #684 + F7 (#1108) — Copy + auto-clear after 60s. Reads no longer carry the
  // full key, so copy fetches it via revealAgentApiKey (POST). The full key is
  // held only inside this function closure. After setLast4, it falls out of
  // scope. The timer clears the clipboard only if it still contains this exact
  // key, so user changes made during the 60s window are preserved.
  const handleCopyKey = async () => {
    setCopying(true);
    try {
      const key = await revealAgentApiKey(agent.id);
      if (typeof key !== 'string' || key.length === 0) {
        showToast('复制失败, 请手动选择 mask 后的 key 复制片段');
        return;
      }
      const ok = await copyToClipboard(key);
      if (!ok) return;
      setLast4(key.slice(-4));
      showToast('API Key 已复制, 60 秒后自动清空');
      // 启动 60s auto-clear: closure 捕获本次 key, setTimeout 触发时
      // readText 比对 — 只在还是这把 key 时清.
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
            showToast('剪贴板已清空 (安全保护)');
          }
          // If it differs, the user copied something else; leave the clipboard alone.
        } catch {
          // readText may be denied (Firefox denies by default). In that case,
          // do not clear anything; blindly writing '' could erase user-copied content.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      // Fallback for browsers without clipboard support or non-HTTPS contexts.
      // document.execCommand is deprecated but remains the available fallback
      // here, avoiding an extra third-party dependency.
      try {
        const key = await revealAgentApiKey(agent.id);
        const ta = document.createElement('textarea');
        ta.value = key;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          setLast4(key.slice(-4));
          showToast('API Key 已复制, 60 秒后自动清空');
        } else {
          showToast('复制失败, 请手动选择 mask 后的 key 复制片段');
        }
      } catch {
        showToast('复制失败, 请手动选择 mask 后的 key 复制片段');
      }
      void err;
    } finally {
      setCopying(false);
    }
  };

  // Mirrors handleCopyKey's security posture: the full API key never enters
  // React state — it's fetched on demand, folded into the connect command in
  // this closure, written to the clipboard, and then discarded. Reuses the
  // same 60s auto-clear pattern (compares clipboard contents before wiping).
  const handleCopyConnectCommand = async () => {
    setCopyingConnect(true);
    try {
      const key = await revealAgentApiKey(agent.id);
      if (typeof key !== 'string' || key.length === 0) {
        showToast('复制失败, 请手动选择 mask 后的 key 复制片段');
        return;
      }
      const command = buildAgentConnectCommand(
        connectProvider,
        getBorgeeHttpBaseUrl(),
        key,
        agent.display_name,
        connectPlatform,
      );
      await copyToClipboard(command);
      setLast4(key.slice(-4));
      setStoredAgentProvider(agent.id, connectProvider);
      showToast('连接命令已复制 (含 API Key), 60 秒后自动清空剪贴板');
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedCommand = command;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedCommand) {
            await copyToClipboard('');
            showToast('剪贴板已清空 (安全保护)');
          }
        } catch {
          // readText may be denied; leave the clipboard untouched.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '复制失败, 请手动选择 mask 后的 key 复制片段');
    } finally {
      setCopyingConnect(false);
    }
  };

  const handleRotateKey = async () => {
    try {
      const result = await rotateAgentApiKey(agent.id);
      const key = result.api_key;
      // Do not put the full key in the DOM; write it to the clipboard and update the mask.
      await copyToClipboard(key);
      setLast4(key.slice(-4));
      showToast('API Key 已复制, 60 秒后自动清空');
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
            showToast('剪贴板已清空 (安全保护)');
          }
        } catch {
          // If readText is denied, leave the clipboard untouched.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '复制失败, 请手动选择 mask 后的 key 复制片段');
    }
  };

  // require_mention toggle handler — optimistic update + rollback on error.
  // 不走 onRefresh re-fetch (整个 list 一次 GET 浪费), 直接用 PATCH 返
  // updated agent 校准本地 state.
  const handleToggleRequireMention = async () => {
    if (savingRequireMention) return;
    const prev = requireMention;
    const next = !prev;
    setRequireMention(next); // optimistic
    setSavingRequireMention(true);
    try {
      const updated = await updateAgentRequireMention(agent.id, next);
      // Server 返回 authoritative 值, 跟我们 optimistic 校准.
      setRequireMention(updated.require_mention ?? next);
      showToast(next ? '已开启: 仅 @mention 时响应' : '已关闭: 任何消息都响应');
      // 通知 parent 同步 (list 重渲不丢字段).
      onRefresh();
    } catch (err) {
      setRequireMention(prev); // 回滚
      showToast(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSavingRequireMention(false);
    }
  };

  const handleTogglePerm = async (perm: string) => {
    const has = permissions.some(p => p.permission === perm && p.scope === '*');
    let newPerms: { permission: string; scope?: string }[];
    if (has) {
      newPerms = permissions.filter(p => !(p.permission === perm && p.scope === '*')).map(p => ({ permission: p.permission, scope: p.scope }));
    } else {
      newPerms = [...permissions.map(p => ({ permission: p.permission, scope: p.scope })), { permission: perm, scope: '*' }];
    }
    try {
      await updateAgentPermissions(agent.id, newPerms);
      await loadPerms();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed');
    }
  };

  const handleJoinChannel = async () => {
    if (!joinChannelId) return;
    try {
      await addAgentToChannel(joinChannelId, agent.id);
      setJoinChannelId('');
      alert('Agent added to channel');
      await loadAgentChannelSettings();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed');
    }
  };

  const handleSaveChannelStrategy = async (channelId: string) => {
    const strategy = channelStrategyDrafts[channelId];
    if (!strategy) return;
    setSavingChannelStrategyId(channelId);
    try {
      await setAgentChannelProjectionStrategy(agent.id, channelId, strategy);
      await loadAgentChannelSettings();
      showToast('Projection strategy saved');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save projection strategy');
    } finally {
      setSavingChannelStrategyId(null);
    }
  };

  return (
    <div className="agent-card">
      {/* #684 — Identity card (header): avatar, name, state, ID, Created, and top buttons. */}
      <section className="agent-detail-card agent-detail-card-identity">
        <div className="admin-card-row">
          <div className="admin-card-info" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <UserBadge
              userId={agent.id}
              displayName={agent.display_name}
              role="agent"
              avatarUrl={agent.avatar_url}
              size={32}
              state={agent.state}
            />
          </div>
          <div className="admin-card-actions">
            <button className="btn btn-sm" onClick={onToggle}>{expanded ? 'Collapse' : 'Manage'}</button>
            <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
          </div>
        </div>
      </section>

      {expanded && (
        <>
          {/* #684 — Credentials card. Show the `bgr_...{last4}` mask by default,
              with no Show button. The full plaintext key never enters the DOM;
              copy fetches on demand, writes to clipboard, auto-clears after 60s,
              and first verifies clipboard contents with readText. */}
          <section className="agent-detail-card agent-detail-card-credentials">
            <strong>API Key</strong>
            <div className="api-key-box" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span data-testid="agent-api-key-mask" style={{ flex: 1 }}>
                {loadingKey ? '加载中...' : (last4 ? formatMaskedApiKey(last4) : '加载中...')}
              </span>
              <button
                className="btn-icon"
                onClick={handleCopyKey}
                disabled={copying || loadingKey}
                aria-label="复制 API Key"
                title="复制完整 API Key 到剪贴板"
              >
                {copying ? '...' : '📋'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-sm" onClick={handleRotateKey}>Rotate API Key</button>
            </div>
          </section>

          {/* Connect card — pick a provider and copy the ready-to-run
              agents-host/openclaw command for this agent, without ever
              putting the full API key in the DOM (same posture as the
              credentials card above: fetched on demand, written straight
              to the clipboard, auto-cleared after 60s). */}
          <section className="agent-detail-card agent-detail-card-connect">
            <strong>Connect</strong>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              上次选择: {AGENT_PROVIDER_LABELS[connectProvider]}
              <span style={{ marginLeft: 4 }}>(本地记忆, 非实际连接状态检测)</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {connectProvider === 'openclaw'
                ? 'Copy these OpenClaw commands for the target computer that should host this agent, then run them with OpenClaw installed there.'
                : providerHasPlatformDifference(connectProvider)
                  ? 'Copy these commands for the target computer that should host this agent, then run them with the required runtime installed there.'
                  : 'Copy this POSIX-shell command for the target computer (macOS/Linux/WSL/Git Bash) that should host this agent, then run it with the CLI installed there.'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                className="input-field"
                value={connectProvider}
                onChange={e => setConnectProvider(e.target.value as AgentProvider)}
                aria-label="Connect provider"
              >
                {AGENT_PROVIDERS.map(p => (
                  <option key={p} value={p}>{AGENT_PROVIDER_LABELS[p]}</option>
                ))}
              </select>
              <button className="btn btn-sm" onClick={handleCopyConnectCommand} disabled={copyingConnect}>
                {copyingConnect ? '...' : '📋 Copy connect command'}
              </button>
            </div>
            {providerHasPlatformDifference(connectProvider) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className={`btn btn-sm${connectPlatform === 'windows' ? ' btn-primary' : ''}`}
                  onClick={() => setConnectPlatform('windows')}
                >
                  Windows
                </button>
                <button
                  type="button"
                  className={`btn btn-sm${connectPlatform === 'linux' ? ' btn-primary' : ''}`}
                  onClick={() => setConnectPlatform('linux')}
                >
                  Linux / macOS
                </button>
              </div>
            )}
            {(() => {
              const previewBlocks = buildAgentConnectCommandParts(
                connectProvider,
                getBorgeeHttpBaseUrl(),
                last4 ? formatMaskedApiKey(last4) : 'your-agent-api-key',
                agent.display_name,
                connectPlatform,
              );
              return (
                <>
                  {previewBlocks.map((block, i) => (
                    <pre
                      key={i}
                      {...(i === previewBlocks.length - 1 ? { 'data-testid': 'agent-connect-command-preview' } : {})}
                      style={{
                        background: 'var(--bg-secondary, #f5f5f0)',
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 8,
                        fontSize: 12,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                      }}
                    >
                      {block}
                    </pre>
                  ))}
                </>
              );
            })()}
          </section>



          {/* Mention 设置 — owner-rail PATCH /api/v1/agents/{id} 翻动
              users.require_mention. true (默认) = 仅 mention 时响应,
              false = 任何消息都响应. 不跟频道级 ChannelMentionControls 共
              逻辑 (那是 per-channel override). */}
          <section className="agent-detail-card agent-detail-card-mention">
            <strong>Mention 设置</strong>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                fontSize: 13,
                cursor: savingRequireMention ? 'progress' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={requireMention}
                disabled={savingRequireMention}
                onChange={handleToggleRequireMention}
                data-testid="agent-require-mention-toggle"
              />
              <span>仅 @mention 时响应</span>
              {savingRequireMention && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>保存中…</span>
              )}
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {requireMention
                ? '当前: 仅在被 @mention 时响应消息'
                : '当前: 任何消息都会触发响应'}
            </p>
          </section>

          {/* #684 — Runtime card. AL-4.3 (#379 §1 split): fetchAgentRuntime null
              means graceful degradation, so omit the card (design ① "Borgee 不带 runtime").
              RuntimeCard handles the owner-only DOM gate: non-owners can see the
              status badge but not start/stop buttons (#321 §2). */}
          {runtimeLoaded && (
            <section className="agent-detail-card agent-detail-card-runtime">
              <RuntimeCard
                agent={agent}
                runtime={runtime}
                viewerUserID={viewerUserID}
                onRefresh={loadRuntime}
              />
            </section>
          )}

          {/* #684 — Config card. AL-2a.3 (#447 + #480 mount): agent config SSOT
              editor (name / avatar / prompt / model / capabilities / enabled /
              memory_ref). Blueprint §1.4 defines the SSOT boundary; server-side
              allowedConfigKeys fail closed. Runtime-only fields (api_key /
              temperature / token_limit / retry_policy) are not rendered here.
              AgentConfigPanel already includes the "Agent 配置" heading. */}
          <section className="agent-detail-card agent-detail-card-config">
            <AgentConfigPanel agentId={agent.id} />
          </section>

          {managedProvider === 'claude' && (
            <section className="agent-detail-card agent-detail-card-claude-channel-strategy">
              <strong>Projection Strategy</strong>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                One setting per top-level channel. Task threads inherit the parent channel strategy.
              </p>
              {loadingAgentChannels ? (
                <p style={{ marginTop: 8 }}>Loading...</p>
              ) : agentChannels.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                  Add this agent to a channel to manage projection strategy.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  {agentChannels.map(channel => {
                    const draftStrategy = channelStrategyDrafts[channel.id] ?? channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY;
                    const savedStrategy = channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY;
                    const saving = savingChannelStrategyId === channel.id;
                    return (
                      <div
                        key={channel.id}
                        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8, alignItems: 'center' }}
                      >
                        <span>#{channel.name}</span>
                        <select
                          className="input-field"
                          value={draftStrategy}
                          onChange={e => setChannelStrategyDrafts(prev => ({
                            ...prev,
                            [channel.id]: e.target.value as ClaudePromptStrategy,
                          }))}
                          aria-label={`Projection strategy for ${channel.name}`}
                        >
                          {CLAUDE_PROMPT_STRATEGIES.map(strategy => (
                            <option key={strategy} value={strategy}>{strategy}</option>
                          ))}
                        </select>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleSaveChannelStrategy(channel.id)}
                          disabled={saving || draftStrategy === savedStrategy}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* #684 — Permissions 卡 (第 5 卡). */}
          <section className="agent-detail-card agent-detail-card-permissions">
            <strong>Permissions</strong>
            {loadingPerms ? (
              <p>Loading...</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {KNOWN_PERMISSIONS.map(perm => {
                  const has = permissions.some(p => p.permission === perm && p.scope === '*');
                  return (
                    <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                      <input type="checkbox" checked={has} onChange={() => handleTogglePerm(perm)} />
                      {perm}
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          {/* #684 — Channels 卡 (第 6 卡, 加入频道). */}
          <section className="agent-detail-card agent-detail-card-channels">
            <strong>Add to Channel</strong>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <select className="input-field" value={joinChannelId} onChange={e => setJoinChannelId(e.target.value)} style={{ flex: 1 }}>
                <option value="">Select channel...</option>
                {channels.filter(c => c.name !== 'general' && (!c.type || c.type === 'channel')).map(c => (
                  <option key={c.id} value={c.id}>#{channelDisplayName(c)}</option>
                ))}
              </select>
              <button className="btn btn-primary btn-sm" onClick={handleJoinChannel} disabled={!joinChannelId}>Add</button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export function CreateAgentModal({ onClose, onCreated }: { onClose: () => void; onCreated: (agentId?: string) => void }) {
  type WizardStep = 'choose' | 'has-agent' | 'create-openclaw' | 'create-kosmos' | 'create-cli' | 'create-cli-manual' | 'create-hosted' | 'result';
  const navigate = useNavigate();
  const guildId = useAuthStore(s => s.guildId);
  const currentUserId = useAuthStore(s => s.currentUser?.id);
  const ownerPermissions = useAuthStore(s => s.permissions);
  const [step, setStep] = useState<WizardStep>('choose');
  const [displayName, setDisplayName] = useState('');
  const [provider, setProvider] = useState<AgentProvider>('claude');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState('');
  const [resultType, setResultType] = useState<'openclaw' | 'cli' | 'hosted'>('cli');
  const [platform, setPlatform] = useState<Platform>('windows');
  const [availableServers, setAvailableServers] = useState<Guild[]>([]);
  const [selectedServerId, setSelectedServerId] = useState('');
  const [availableChannels, setAvailableChannels] = useState<Channel[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());
  const [configurationFailure, setConfigurationFailure] = useState<string | null>(null);
  const [membershipFailure, setMembershipFailure] = useState<{ stage: 'server' | 'channels'; message: string; channelIds: string[] } | null>(null);
  const [connectedNodes, setConnectedNodes] = useState<RemoteNode[]>([]);
  const [loadingConnectedNodes, setLoadingConnectedNodes] = useState(false);
  const [connectedNodesError, setConnectedNodesError] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [selectedHostedProvider, setSelectedHostedProvider] = useState<HostedAgentProvider>('claude');
  const [createdComputerName, setCreatedComputerName] = useState('');

  const agentPresence = usePresence(createdId ?? undefined);
  const isAgentOnline = agentPresence?.state === 'online';
  const isCliCreateStep = step === 'create-cli' || step === 'create-cli-manual';
  const isHostedCreateStep = step === 'create-hosted';
  const isCreateStep = isCliCreateStep || step === 'create-openclaw' || step === 'create-kosmos' || isHostedCreateStep;

  useEffect(() => {
    if (!isCreateStep || !currentUserId) return;
    void fetchGuilds()
      .then(guilds => setAvailableServers(guilds.filter(guild => guild.owner_id === currentUserId)))
      .catch(() => setAvailableServers([]));
  }, [currentUserId, isCreateStep]);

  useEffect(() => {
    if (!selectedServerId) {
      setAvailableChannels([]);
      setSelectedChannelIds(new Set());
      return;
    }
    void fetchGuildChannels(selectedServerId).then(({ channels: next }) => {
      const canManageMembers = (channelId: string) => ownerPermissions?.some(permission =>
        (permission.permission === '*' && permission.scope === '*') ||
        (permission.permission === 'channel.manage_members' &&
          (permission.scope === '*' || permission.scope === `channel:${channelId}`)),
      ) ?? false;
      setAvailableChannels(next.filter(channel =>
        (!channel.type || channel.type === 'channel') && channel.is_member && canManageMembers(channel.id),
      ));
      setSelectedChannelIds(new Set());
    }).catch(() => {
      setAvailableChannels([]);
      setSelectedChannelIds(new Set());
    });
  }, [ownerPermissions, selectedServerId]);

  const loadConnectedNodes = useCallback(async () => {
    setLoadingConnectedNodes(true);
    setConnectedNodesError('');
    try {
      const nodes = await fetchRemoteNodes();
      const statuses = await Promise.all(
        nodes.map(async (node) => ({
          node,
          status: await fetchRemoteNodeStatus(node.id),
        })),
      );
      const onlineNodes = statuses
        .filter(({ status }) => status.online)
        .map(({ node }) => node);
      setConnectedNodes(onlineNodes);
      setSelectedNodeId((current) =>
        onlineNodes.some((node) => node.id === current) ? current : (onlineNodes[0]?.id ?? ''),
      );
    } catch (err) {
      setConnectedNodes([]);
      setSelectedNodeId('');
      setConnectedNodesError(
        err instanceof Error ? err.message : 'Could not load your connected computers.',
      );
    } finally {
      setLoadingConnectedNodes(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 'create-hosted') {
      return;
    }
    void loadConnectedNodes();
  }, [loadConnectedNodes, step]);

  const applySelectedMemberships = async (agentId: string, retry?: { stage: 'server' | 'channels'; channelIds: string[] }) => {
    const channelIds = retry?.channelIds ?? Array.from(selectedChannelIds);
    if (selectedServerId && retry?.stage !== 'channels') {
      try {
        await addGuildMember(selectedServerId, agentId);
      } catch (err) {
        setMembershipFailure({
          stage: 'server',
          message: err instanceof Error ? err.message : 'Could not add the agent to the Project.',
          channelIds,
        });
        return false;
      }
    }
    for (let index = 0; index < channelIds.length; index++) {
      try {
        await addAgentToChannel(channelIds[index]!, agentId);
      } catch (err) {
        setMembershipFailure({
          stage: 'channels',
          message: err instanceof Error ? err.message : 'Could not add the agent to the selected channels.',
          channelIds: channelIds.slice(index),
        });
        return false;
      }
    }
    setMembershipFailure(null);
    return true;
  };

  const showCreateResult = (agent: Agent, trimmedName: string) => {
    if (step !== 'create-hosted' && !agent.api_key) {
      onCreated(agent.id);
      return;
    }
    if (step === 'create-hosted') {
      setCreatedComputerName(connectedNodes.find((node) => node.id === selectedNodeId)?.machine_name ?? '');
      setResultType('hosted');
    }
    if (agent.api_key) setCreatedKey(agent.api_key);
    setCreatedId(agent.id);
    setCreatedName(trimmedName);
    setStep('result');
  };

  const configureHostedAgent = async (agentId: string) => {
    try {
      await updateAgentConfig(agentId, {
        hosted: { node_id: selectedNodeId, provider: selectedHostedProvider },
      });
      setConfigurationFailure(null);
      return true;
    } catch (err) {
      setConfigurationFailure(
        err instanceof Error ? err.message : 'Could not configure the hosted agent.',
      );
      return false;
    }
  };

  const handleCreate = async () => {
    if (!displayName.trim() || saving) return;
    const trimmedName = displayName.trim();
    setSaving(true);
    setError('');
    setConfigurationFailure(null);
    setMembershipFailure(null);
    try {
      const created = await createAgent(trimmedName, ['message.send']);
      const agent = created.agent;
      setCreatedId(agent.id);
      setCreatedName(trimmedName);
      if (agent.api_key) setCreatedKey(agent.api_key);
      if (step === 'create-hosted') {
        setResultType('hosted');
        setCreatedComputerName(connectedNodes.find((node) => node.id === selectedNodeId)?.machine_name ?? '');
      }
      const selectedProvider = step === 'create-hosted' ? selectedHostedProvider : provider;
      setStoredAgentProvider(agent.id, selectedProvider);
      useAgentStore.getState().loadAgents();
      if (step === 'create-hosted' && !(await configureHostedAgent(agent.id))) {
        return;
      }
      if (!(await applySelectedMemberships(agent.id))) {
        return;
      }
      const currentGuildId = useAuthStore.getState().guildId;
      if (currentGuildId) void Promise.resolve(useServerStore.getState().loadMembers(currentGuildId)).catch(() => undefined);
      showCreateResult(agent, trimmedName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = (selected?: boolean): React.CSSProperties => ({
    padding: 16,
    borderRadius: 8,
    border: `2px solid ${selected ? 'var(--accent-primary, #5865f2)' : 'var(--border-color, #e0e0e0)'}`,
    cursor: 'pointer',
    background: selected ? 'var(--bg-hover, #f0f0ff)' : 'var(--bg-primary, #fff)',
    transition: 'all 0.15s',
  });

  const disabledCardStyle: React.CSSProperties = {
    ...cardStyle(),
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  const MembershipFields = () => (
    <fieldset style={{ border: 0, padding: 0, margin: '12px 0 0' }}>
      <label style={{ display: 'block' }}>
        Project (optional)
        <select className="input-field" value={selectedServerId} onChange={event => setSelectedServerId(event.target.value)}>
          <option value="">No Project</option>
          {availableServers.map(guild => <option key={guild.id} value={guild.id}>{guild.name}</option>)}
        </select>
      </label>
      {selectedServerId && <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Channels (optional)</div>
        {availableChannels.map(channel => <label key={channel.id} style={{ display: 'block', fontSize: 13 }}>
          <input type="checkbox" checked={selectedChannelIds.has(channel.id)} onChange={event => setSelectedChannelIds(previous => {
            const next = new Set(previous);
            if (event.target.checked) next.add(channel.id); else next.delete(channel.id);
            return next;
          })} /> #{channelDisplayName(channel)}
        </label>)}
      </div>}
    </fieldset>
  );

  if (configurationFailure && createdId) {
    return (
      <div className="admin-modal" onClick={onClose}>
        <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <h3>Agent created; configuration failed</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {createdName || 'Your agent'} was created and was not deleted. {configurationFailure}
          </p>
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-sm" onClick={() => onCreated(createdId)}>Close</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={async () => {
              setSaving(true);
              const configured = await configureHostedAgent(createdId);
              if (configured && await applySelectedMemberships(createdId)) setStep('result');
              setSaving(false);
            }}>{saving ? 'Retrying...' : 'Retry configuration'}</button>
          </div>
        </div>
      </div>
    );
  }

  if (membershipFailure && createdId) {
    return (
      <div className="admin-modal" onClick={onClose}>
        <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <h3>Agent created; membership update failed</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {createdName || 'Your agent'} was created and was not deleted. {membershipFailure.message}
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {membershipFailure.stage === 'server' ? 'Retry adding the agent to the Project before its selected channels.' : 'Retry the channels that were not added.'}
          </p>
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-sm" onClick={() => onCreated(createdId)}>Close</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={async () => {
              setSaving(true);
              const placed = await applySelectedMemberships(createdId, membershipFailure);
              if (placed) setStep('result');
              setSaving(false);
            }}>{saving ? 'Retrying...' : 'Retry membership'}</button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Result
  if (step === 'result' && createdId && (resultType === 'hosted' || createdKey)) {
    if (resultType === 'hosted') {
      return (
        <div className="admin-modal" onClick={() => onCreated(createdId ?? undefined)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3>Starting {createdName || 'your agent'}</h3>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, marginBottom: 8 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                Borgee asked <b>{createdComputerName || 'your computer'}</b> to start <b>{createdName}</b> with {AGENT_PROVIDER_LABELS[selectedHostedProvider]}.
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              {isAgentOnline ? (
                <>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}><b>{createdName}</b> connected</span>
                </>
              ) : (
                <>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--text-secondary, #888)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, animation: 'spin 1.5s linear infinite' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Waiting for <b>{createdName}</b> to connect...</span>
                </>
              )}
            </div>

            <button
              className={`btn btn-sm${isAgentOnline ? ' btn-primary' : ''}`}
              style={{ width: '100%', marginTop: 16, ...(isAgentOnline ? {} : { color: 'var(--text-secondary)' }) }}
              onClick={async () => {
                if (isAgentOnline) {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId) navigate(routes.member(guildId, createdId) + '?tab=chat');
                } else {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId) navigate(routes.member(guildId, createdId) + '?tab=profile');
                }
              }}
            >
              {isAgentOnline ? 'Done' : 'Skip, I\'ll do this later'}
            </button>
          </div>
        </div>
      );
    }
    if (!createdKey) {
      return null;
    }
    if (resultType === 'openclaw') {
      const ownerName = useAuthStore.getState().currentUser?.display_name ?? 'Someone';
      const serverUrl = getBorgeeHttpBaseUrl();
      const invitationText = `📮 Invitation to ${createdName}\n\nDear ${createdName},\n\n${ownerName} built this place, and chose you to share it.\nThere are channels to discover, routines to build,\nand conversations that haven't started yet.\n\n— ${ownerName}\n\n════════════════════════════════════════\n${buildOpenClawOnboardingPlan({ serverUrl, apiKey: createdKey })}\n\n════════════════════════════════════════\nAfter restart, you'll be connected automatically.`;

      return (
        <div className="admin-modal" onClick={() => onCreated(createdId ?? undefined)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3>Connect {createdName || 'your agent'}</h3>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, marginBottom: 8 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Copy this invitation and send it to <b>{createdName}</b></span>
            </div>
            <div style={{ marginLeft: 28 }}>
              <div style={{ position: 'relative', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6, padding: 10, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 16, maxHeight: 200, overflowY: 'auto', lineHeight: 1.5 }}>
                {invitationText}
                <button
                  className="btn btn-sm"
                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, padding: '2px 6px' }}
                  onClick={() => { copyToClipboard(invitationText); }}
                >
                  <CopyOutlined />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              {isAgentOnline ? (
                <>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}><b>{createdName}</b> connected</span>
                </>
              ) : (
                <>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--text-secondary, #888)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Waiting for <b>{createdName}</b> to connect...</span>
                </>
              )}
            </div>

            <button
              className={`btn btn-sm${isAgentOnline ? ' btn-primary' : ''}`}
              style={{ width: '100%', marginTop: 16, ...(isAgentOnline ? {} : { color: 'var(--text-secondary)' }) }}
              onClick={async () => {
                if (isAgentOnline) {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId) navigate(routes.member(guildId, createdId) + '?tab=chat');
                } else {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId) navigate(routes.member(guildId, createdId) + '?tab=profile');
                }
              }}
            >
              {isAgentOnline ? 'Done' : 'Skip, I\'ll do this later'}
            </button>
          </div>
        </div>
      );
    }
    const serverUrl = getBorgeeHttpBaseUrl();
    const cmdBlocks = buildAgentConnectCommandParts(provider, serverUrl, createdKey, createdName, platform);
    return (
      <div className="admin-modal" onClick={() => onCreated(createdId ?? undefined)}>
        <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
          <h3 data-testid="create-agent-result-status">Connect {createdName || 'your agent'}</h3>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, marginBottom: 8 }}>
            <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Run these commands on the machine where <b>{createdName}</b> will run</span>
          </div>
          {providerHasPlatformDifference(provider) && (
            <div style={{ marginLeft: 28, marginBottom: 4 }}>
              <Tabs
                size="small"
                activeKey={platform}
                onChange={(key: string) => setPlatform(key as Platform)}
                items={[
                  { key: 'windows', label: 'Windows' },
                  { key: 'linux', label: 'Linux / macOS' },
                ]}
              />
            </div>
          )}
          <div style={{ marginLeft: 28 }}>
            {cmdBlocks.map((block, i) => (
              <div key={i} style={{ position: 'relative', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6, padding: 10, fontSize: 12, fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: i < cmdBlocks.length - 1 ? 8 : 16 }}>
                {block}
                <button
                  className="btn btn-sm"
                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, padding: '2px 6px' }}
                  onClick={() => { copyToClipboard(block); }}
                >
                  <CopyOutlined />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
            {isAgentOnline ? (
              <>
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--color-success, #22c55e)', color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>&#10003;</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}><b>{createdName}</b> connected</span>
              </>
            ) : (
              <>
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--text-secondary, #888)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, animation: 'spin 1.5s linear infinite' }} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Waiting for <b>{createdName}</b> to connect...</span>
              </>
            )}
          </div>

          <button
            className={`btn btn-sm${isAgentOnline ? ' btn-primary' : ''}`}
            style={{ width: '100%', marginTop: 16, ...(isAgentOnline ? {} : { color: 'var(--text-secondary)' }) }}
            onClick={() => {
              onCreated(createdId ?? undefined);
              if (guildId && createdId) navigate(routes.member(guildId, createdId) + (isAgentOnline ? '?tab=chat' : '?tab=profile'));
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-modal" onClick={onClose}>
      <div className="admin-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>

        {/* Step 1: Choose path */}
        {step === 'choose' && (
          <>
            <h3>Add an Agent</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>How would you like to add an agent to this Project?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={cardStyle()} onClick={() => setStep('has-agent')}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>🤖 I already have an agent</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Connect Claude Code, GitHub Copilot, Codex, DeepSeek Harness, OpenClaw, Hermes, PraestoClaw, or Kosmos Desktop to Borgee</div>
              </div>
              <div style={cardStyle()} onClick={() => setStep('create-hosted')}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>🖥️ Create an agent on my machine</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Choose one of your connected computers and let Borgee start Claude, Codex, or Copilot there</div>
              </div>
              <div style={disabledCardStyle}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>☁️ Create a cloud agent</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Coming soon</div>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* Step 2a: What kind of agent */}
        {step === 'has-agent' && (
          <>
            <h3>What agent do you have?</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={cardStyle()} onClick={() => { setProvider('claude'); setResultType('cli'); setDisplayName('Claude Code'); setStep('create-cli'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Claude Code</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('copilot'); setResultType('cli'); setDisplayName('GitHub Copilot'); setStep('create-cli'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>GitHub Copilot</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('codex'); setResultType('cli'); setDisplayName('Codex'); setStep('create-cli'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Codex</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('dsh'); setResultType('cli'); setDisplayName('DeepSeek'); setStep('create-cli-manual'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>DeepSeek Harness</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('openclaw'); setResultType('openclaw'); setStep('create-openclaw'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>OpenClaw</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('hermes'); setResultType('cli'); setDisplayName('Hermes'); setStep('create-cli-manual'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Hermes Agent</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('praestoclaw'); setResultType('cli'); setDisplayName('PraestoClaw'); setStep('create-cli-manual'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>PraestoClaw</div>
              </div>
              <div style={cardStyle()} onClick={() => { setProvider('kosmos'); setResultType('cli'); setDisplayName('Kosmos'); setStep('create-kosmos'); }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Kosmos Desktop</div>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-sm" onClick={() => setStep('choose')}>← Back</button>
            </div>
          </>
        )}

        {/* Step 2a-1: Create OpenClaw agent */}
        {step === 'create-openclaw' && (
          <>
            <h3>Name your agent</h3>
            <form onSubmit={e => { e.preventDefault(); handleCreate(); }}>
              <label>
                Agent Name
                <input className="input-field" value={displayName} onChange={e => setDisplayName(e.target.value)} required autoFocus placeholder="e.g. Kagura" />
              </label>
              <MembershipFields />
              {error && <div className="admin-form-error" style={{ marginTop: 8 }}>{error}</div>}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>← Back</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !displayName.trim()}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2a-2: Create Kosmos agent */}
        {step === 'create-kosmos' && (
          <>
            <h3>Name your agent</h3>
            <form onSubmit={e => { e.preventDefault(); handleCreate(); }}>
              <label>
                Agent Name
                <input className="input-field" value={displayName} onChange={e => setDisplayName(e.target.value)} required autoFocus placeholder="e.g. Kosmos" />
              </label>
              <MembershipFields />
              {error && <div className="admin-form-error" style={{ marginTop: 8 }}>{error}</div>}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>← Back</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !displayName.trim()}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2a-3: Create CLI agent */}
        {isCliCreateStep && (
          <>
            <h3>Name your agent</h3>
            <form onSubmit={e => { e.preventDefault(); handleCreate(); }}>
              <label>
                Agent Name
                <input className="input-field" value={displayName} onChange={e => setDisplayName(e.target.value)} required autoFocus placeholder="e.g. Alice" />
              </label>
              <MembershipFields />
              {error && <div className="admin-form-error" style={{ marginTop: 8 }}>{error}</div>}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>← Back</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !displayName.trim()}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </>
        )}

        {isHostedCreateStep && (
          <>
            <h3>Create an agent on my machine</h3>
            <form onSubmit={e => { e.preventDefault(); void handleCreate(); }}>
              <label>
                Agent Name
                <input className="input-field" value={displayName} onChange={e => setDisplayName(e.target.value)} required autoFocus placeholder="e.g. Codex Builder" />
              </label>
              <MembershipFields />
              <label style={{ display: 'block', marginTop: 12 }}>
                Computer
                <select
                  className="input-field"
                  data-testid="create-agent-computer-select"
                  value={selectedNodeId}
                  onChange={e => setSelectedNodeId(e.target.value)}
                  disabled={loadingConnectedNodes || connectedNodes.length === 0}
                >
                  {connectedNodes.length === 0 ? (
                    <option value="">
                      {loadingConnectedNodes ? 'Loading connected computers…' : 'No connected computers'}
                    </option>
                  ) : (
                    connectedNodes.map((node) => (
                      <option key={node.id} value={node.id}>{node.machine_name}</option>
                    ))
                  )}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 12 }}>
                Runtime
                <select
                  className="input-field"
                  value={selectedHostedProvider}
                  onChange={e => setSelectedHostedProvider(e.target.value as HostedAgentProvider)}
                >
                  {HOSTED_AGENT_PROVIDERS.map((hostedProvider) => (
                    <option key={hostedProvider} value={hostedProvider}>
                      {AGENT_PROVIDER_LABELS[hostedProvider]}
                    </option>
                  ))}
                </select>
              </label>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 0 }}>
                Only computers that are connected right now can host a managed agent.
              </p>
              {(error || connectedNodesError) && <div className="admin-form-error" style={{ marginTop: 8 }}>{error || connectedNodesError}</div>}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('choose')}>← Back</button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !displayName.trim() || !selectedNodeId || loadingConnectedNodes}
                >
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </>
        )}

      </div>
    </div>
  );
}
